import { matchTransfers, normalizeTransactions } from './transferMatcher';
import { INCOME_TYPES } from './transactionTypes';
import { D, D_ZERO, D_EPSILON, Decimal } from './decimalMath';
import { resolveSwapEurValue } from './stablecoinResolver';
import { tsMillis, tsPrecisionRank } from './txFingerprint';

// Fiat-Quote-Coins: bei Swaps gegen Fiat (XLM↔EUR) ist mengeIn/mengeOut direkt
// der EUR-Wert — der Stablecoin-Resolver kennt nur USDC/USDT/DAI/EURC. Diese
// Fallback-Logik macht alte typ='swap'-TXs aus Binance (Tx History pre-fix)
// auch korrekt berechenbar, ohne Re-Import.
const FIFO_FIAT_QUOTES = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

// DE Spekulationsfrist (§23 EStG + BGB §188 Abs. 2):
// Die Frist endet mit Ablauf des Tages, der durch seine Bezeichnung dem
// Anschaffungstag entspricht. Verkauf AM Jahrestag ist noch innerhalb der
// Frist (steuerpflichtig); steuerfrei erst ab dem Folgetag.
// Beispiel: Kauf 2024-01-15 → tax-frei ab Verkauf 2025-01-16.
function isWithinSpekulationsfrist(buyDateStr, sellDateStr) {
  if (!buyDateStr || !sellDateStr) return false;
  const buy = new Date(buyDateStr);
  if (isNaN(buy.getTime())) return false;
  const ann = new Date(Date.UTC(buy.getUTCFullYear() + 1, buy.getUTCMonth(), buy.getUTCDate()));
  const annStr = ann.toISOString().slice(0, 10);
  const sellStr = String(sellDateStr).slice(0, 10);
  return sellStr <= annStr; // taxable wenn Verkauf am/vor Jahrestag
}

// Wallet-Identifier eines TX (oder leerer String). Wird als Lot-Location
// gesetzt — FIFO arbeitet wallet-spezifisch (BMF 10.5.2022, Rn. 61 erlaubt das).
function _loc(tx) {
  // tx.wallet wird von buildWalletEnrichment (moonpayLedgerDedupe) auf Käufe
  // gesetzt, die physisch in eine eigene Wallet geliefert wurden (z.B.
  // MoonPay-Kauf → Phantom/Ledger). Die Coins liegen danach in DIESER Wallet —
  // also ist sie die FIFO-Location, nicht die kaufende Börse. Sonst sucht ein
  // späterer Wallet-Swap (exchange='Phantom') die Coins am falschen Ort
  // (location='MoonPay'), findet nichts und greift via Hybrid-Fallback auf
  // fremde Börsen-Lots (Coinbase/Bitget) zu → falsche Kostenbasis/Haltefrist
  // (reales Symptom: Phantom-DEX-Swap verbrauchte USDC von anderen Börsen).
  // Nur Lieferungen tragen ein wallet-Feld; Verkäufe/Swaps behalten ihre
  // exchange-Location. Bestandsneutral — verschiebt nur WELCHE Lots verbraucht
  // werden, nicht wie viele.
  return String(tx?.wallet || tx?.exchange || tx?.platform || '').trim();
}

// Wallet-spezifische FIFO mit Hybrid-Fallback.
// Wenn in der Wallet-Location genug bewertete Lots verfügbar sind → wallet-spezifisch.
// Wenn NICHT genug Lots ODER nicht genug PREISBEKANNTE Lots → Fallback auf
// wallet-übergreifend, sofern global genug priced Lots existieren.
// Das matched die User-Intuition wenn Daten sauber sind, bleibt aber robust
// bei unvollständigen Transfer-Daten (ältere TXs ohne exchange-Feld o.ä.).
function fifoMatch(pool, coin, menge, datum, location) {
  const target = D(menge);

  let effectiveLocation = location || null;
  if (effectiveLocation) {
    const lots = pool[coin] || [];
    let sumInLoc        = D_ZERO;
    let sumInLocPriced  = D_ZERO;
    for (const lot of lots) {
      if (lot.rest <= 1e-9) continue;
      if (lot.location && lot.location !== effectiveLocation) continue;
      const r = D(lot.rest);
      sumInLoc = sumInLoc.plus(r);
      if (!lot.costBasisUnknown) sumInLocPriced = sumInLocPriced.plus(r);
    }
    // Bedingung 1: Wallet hat NICHT genug Menge → fallback (klassisch)
    // Bedingung 2: Wallet hat genug Menge, aber NICHT genug priced Menge →
    //              fallback wenn global mehr priced Lots verfügbar sind.
    //              Sonst wallet-spezifisch belassen (besser costBasisUnknown
    //              mit korrektem Kaufdatum als false-positive aus fremder Wallet).
    if (sumInLoc.lt(target)) {
      effectiveLocation = null;
    } else if (sumInLocPriced.lt(target)) {
      let sumGlobalPriced = D_ZERO;
      for (const lot of lots) {
        if (lot.rest <= 1e-9) continue;
        if (lot.costBasisUnknown) continue;
        sumGlobalPriced = sumGlobalPriced.plus(D(lot.rest));
        if (sumGlobalPriced.gte(target)) break;
      }
      if (sumGlobalPriced.gt(sumInLocPriced)) {
        effectiveLocation = null;
      }
    }
  }

  let rest = target;
  let ak   = D_ZERO;
  let kd   = null;
  let costBasisUnknownAmount = D_ZERO;
  const soldLots = [];

  for (const lot of (pool[coin] || [])) {
    if (lot.rest <= 1e-9) continue;
    if (effectiveLocation && lot.location && lot.location !== effectiveLocation) continue;
    if (!kd) kd = lot.datum;
    const lotRest = D(lot.rest);
    const use     = Decimal.min(lotRest, rest);
    ak             = ak.plus(use.times(D(lot.stueck)));
    if (lot.costBasisUnknown) {
      costBasisUnknownAmount = costBasisUnknownAmount.plus(use);
    }
    soldLots.push({ datum: lot.datum, stueck: lot.stueck, use: use.toNumber() });
    lot.rest = lotRest.minus(use).toNumber();
    rest     = rest.minus(use);
    if (rest.lte(D_EPSILON)) break;
  }

  const hf = kd ? Math.floor((new Date(datum) - new Date(kd)) / 864e5) : null;
  const matched = target.minus(rest);
  return {
    ak:               ak.toDecimalPlaces(8).toNumber(),
    kd,
    hf,
    soldLots,
    incomplete:       rest.gt(D_EPSILON),
    unmatched:        rest.gt(D_ZERO) ? rest.toNumber() : 0,
    matched:          matched.toNumber(),
    target:           target.toNumber(),
    costBasisUnknown: costBasisUnknownAmount.gt(D_EPSILON),
    fallbackUsed:     !!location && effectiveLocation !== location,
  };
}

// Reserviert Lots für einen Transfer (markiert pending), wallet-spezifisch
// mit gleichem Hybrid-Fallback wie fifoMatch. Gibt unmatched-Rest zurück.
function markForTransfer(pool, coin, menge, outId, fromLocation) {
  const target = D(menge);

  let effectiveLocation = fromLocation || null;
  if (effectiveLocation) {
    const lots = pool[coin] || [];
    let sumInLoc       = D_ZERO;
    let sumInLocPriced = D_ZERO;
    for (const lot of lots) {
      if (lot.rest <= 1e-9) continue;
      if (lot.location && lot.location !== effectiveLocation) continue;
      const r = D(lot.rest);
      sumInLoc = sumInLoc.plus(r);
      if (!lot.costBasisUnknown) sumInLocPriced = sumInLocPriced.plus(r);
    }
    if (sumInLoc.lt(target)) {
      effectiveLocation = null;
    } else if (sumInLocPriced.lt(target)) {
      let sumGlobalPriced = D_ZERO;
      for (const lot of lots) {
        if (lot.rest <= 1e-9) continue;
        if (lot.costBasisUnknown) continue;
        sumGlobalPriced = sumGlobalPriced.plus(D(lot.rest));
        if (sumGlobalPriced.gte(target)) break;
      }
      if (sumGlobalPriced.gt(sumInLocPriced)) effectiveLocation = null;
    }
  }

  let rest = target;
  for (const lot of (pool[coin] || [])) {
    if (lot.rest <= 1e-9) continue;
    if (effectiveLocation && lot.location && lot.location !== effectiveLocation) continue;
    const lotRest = D(lot.rest);
    const use     = Decimal.min(lotRest, rest);
    lot.rest            = lotRest.minus(use).toNumber();
    lot.pendingTransfer = D(lot.pendingTransfer || 0).plus(use).toNumber();
    lot.transferOutId   = outId;
    rest = rest.minus(use);
    if (rest.lte(D_EPSILON)) break;
  }
  return rest.gt(D_ZERO) ? rest.toNumber() : 0;
}

// Stellt Lots eines gematchten Transfers wieder her — UND ÜBERTRÄGT die location
// auf das Ziel-Wallet (wallet-spezifische FIFO: Lots wandern mit ihrem Datum/Preis).
function completeTransfer(pool, coin, outId, toLocation) {
  let restored = D_ZERO;
  for (const lot of (pool[coin] || [])) {
    if (lot.transferOutId !== outId || !(lot.pendingTransfer > 0)) continue;
    const pending  = D(lot.pendingTransfer);
    lot.rest            = D(lot.rest).plus(pending).toNumber();
    restored            = restored.plus(pending);
    lot.pendingTransfer = 0;
    lot.transferOutId   = null;
    if (toLocation) lot.location = toLocation;
  }
  return restored.toNumber();
}

// Bei Überverkauf einen Negativ-Lot anlegen, damit Bestand nicht still verzerrt
// wird. Wird in Reconciliation und Tests als debt-Lot erkannt
// (overConsumption: true, rest < 0).
//
// Warning-Verhalten: in Production keine Console-Logs (Debt-Lots reichen für
// die UI-Reconciliation). In Dev wird pro (coin+txId+source) genau einmal
// gewarnt, damit FIFO bei mehrfacher Pool-Berechnung pro Render die Console
// nicht spammt.
const _warnedOverConsumption = new Set();
function recordOverConsumption(pool, coin, unmatched, tx, source, loc) {
  if (unmatched <= 1e-9) return;
  if (!pool[coin]) pool[coin] = [];
  const txId = tx._docId || String(tx.id);
  if (process.env.NODE_ENV !== 'production') {
    const key = `${coin}|${txId}|${source}`;
    if (!_warnedOverConsumption.has(key)) {
      _warnedOverConsumption.add(key);
      // eslint-disable-next-line no-console
      console.warn(`[fifo] Überverkauf: ${coin} fehlt ${unmatched} bei ${source} (TX ${txId} am ${tx.datum})`);
    }
  }
  pool[coin].push({
    lotId:               `debt_${source}_${txId}`,
    sourceTransactionId: txId,
    datum:               tx.datum,
    rest:                -unmatched,
    originalAmount:      -unmatched,
    stueck:              0,
    location:            loc,
    costBasisUnknown:    true,
    overConsumption:     true,
  });
}

// ── Teil-/Über-Abgang in zwei Events zerlegen (Finanzamt-Nachweis) ─────────────
// Wenn eine Veräußerung den verfügbaren Bestand übersteigt (incomplete), wird sie
// in zwei Events aufgeteilt — analog zur AT-Engine (austrianCost.realise):
//   1) gedeckter Anteil: realer gv aus den verbrauchten Lots (proportionaler Erlös),
//   2) ungedeckter Rest: eigenes Event, Anschaffung offen (costBasisUnknown), gv null.
// Erlös (fullPreisD) und Gebühr (fullFeeD) werden proportional zur Menge verteilt.
// gainOf(netProceedsD, akD) liefert den gv des gedeckten Anteils (Verkauf/Swap:
// Erlös−ak; Verlust: −ak). makeEvent(seg) baut das typ-spezifische Event.
//
// VORHER wurde bei incomplete der GANZE gv auf null gesetzt UND das Event von
// getTaxableEvents herausgefiltert → die Veräußerung (inkl. gedecktem Gewinn)
// verschwand still aus Report & Veräußerungsübersicht, ohne Warnung. Der voll
// gedeckte Normalfall (!incomplete) läuft weiterhin über den unveränderten Pfad.
function emitSplitDisposal(results, mr, tx, fullPreisD, fullFeeD, gainOf, makeEvent) {
  const target    = D(mr.target);
  const matched   = D(mr.matched);
  const unmatched = target.minus(matched);

  if (matched.gt(D_EPSILON)) {
    const prop   = matched.div(target);
    const grossM = fullPreisD.times(prop);
    const feeM   = fullFeeD.times(prop);
    const gv = mr.costBasisUnknown ? null : gainOf(grossM.minus(feeM), D(mr.ak));
    results.push(makeEvent({
      menge:            matched.toNumber(),
      prop,
      preis:            grossM.toDecimalPlaces(2).toNumber(),
      gebuehr:          feeM.toDecimalPlaces(2).toNumber(),
      ak:               D(mr.ak).toDecimalPlaces(2).toNumber(),
      kaufDatum:        mr.kd,
      hf:               mr.hf,
      stpfl:            isWithinSpekulationsfrist(mr.kd, tx.datum),
      gv,
      soldLots:         mr.soldLots,
      costBasisUnknown: mr.costBasisUnknown || undefined,
      fallbackUsed:     mr.fallbackUsed || undefined,
      incomplete:       undefined,
    }));
  }

  if (unmatched.gt(D_EPSILON)) {
    const prop = target.gt(D_ZERO) ? unmatched.div(target) : D(1);
    results.push(makeEvent({
      menge:            unmatched.toNumber(),
      prop,
      preis:            fullPreisD.times(prop).toDecimalPlaces(2).toNumber(),
      gebuehr:          fullFeeD.times(prop).toDecimalPlaces(2).toNumber(),
      ak:               0,
      kaufDatum:        null,
      hf:               null,
      stpfl:            true,             // Anschaffung offen → konservativ steuerpflichtig
      gv:               null,
      soldLots:         [],
      costBasisUnknown: true,
      fallbackUsed:     undefined,
      incomplete:       true,            // surft Warnung „incomplete-match" an
    }));
  }
}

// Typ-Priorität für Sortierung bei gleichem Datum.
//
// Kausale Reihenfolge: kauf → matched-aus → matched-ein → unmatched-ein
//   → income → swap → verkauf → unmatched-aus
//
// WICHTIG: matched-aus (1) und matched-ein (2) MÜSSEN beide VOR swap (5) liegen,
// und matched-aus MUSS vor matched-ein liegen.
// Begründung: wenn Coins per Transfer ankommen und am gleichen Tag geswappt
// werden, muss die Transfer-Kette (aus→ein) KOMPLETT laufen, bevor swap
// die Coins verbraucht.
//
// Alter Wert matched-ein=1 (vor swap=4 und matched-aus=5) erzeugte einen Zyklus:
//   ein(1) < swap(4) < aus(5) per typePrio, ABER aus < ein per Spezialfall →
//   inkonsistenter Komparator → JS-Sort: undefinierte Reihenfolge →
//   ein vor aus → Ghost-Lot 2500 XLM costBasisUnknown (reales Symptom 2026-01-05).
function _typePrio(tx, txId, matchedOuts, matchedIns) {
  switch (tx.typ) {
    case 'kauf':       return 0;
    case 'auszahlung': return matchedOuts.has(txId) ? 1 : 7;
    case 'einzahlung': return matchedIns.has(txId)  ? 2 : 3;
    case 'staking':
    case 'airdrop':
    case 'mining':
    case 'cashback':
    case 'interest':   return 4;
    case 'swap':       return 5;
    case 'verkauf':    return 6;
    default:           return 8;
  }
}

// Sort-Kette (identisch zur ehemaligen lotSystem.js — deterministisch):
//   1. Datum (YYYY-MM-DD) — manuelle Reorder (effectiveTimestamp) hat Vorrang
//   2. typPrio NUR bei Tages-Präzision oder fehlender Zeit, damit am gleichen
//      Tag Eingang vor Ausgang läuft
//   3. Volle ms (ts)
//   4. tsPrecision ('second' < 'minute' < 'day') — genauere Quelle gewinnt
//   5. manualOrderIndex — User-Override
//   6. typPrio (regulär)
//   7. _docId — deterministischer Tiebreaker
// Causal-Predecessor Map: fuer jede TX-ID (matched-aus oder auszahlung mit
// externalTransfer), die einen Coin auszahlt, der am SELBEN Tag in DERSELBEN
// location erst durch einen Swap-IN erzeugt wird, mappe TX-ID → Swap-TX-ID.
// Dies erlaubt dem Sort, die matched-aus NACH dem Swap einzuordnen (sonst
// rennt markForTransfer auf leeren Pool → debt-Lot + Gap-Lot beim einzahlung
// → Doppelbestand. Reales Symptom: Stablecoin-Reste nach Swap+Transfer).
function _buildCausalSwapPredecessors(txs, matchedOutIds) {
  // Map "COIN|LOC|YYYY-MM-DD" -> erster Swap, der diesen Coin am Tag PRODUZIERT
  const swapsByCoinLocDay = new Map();
  for (const tx of txs) {
    if (tx.typ !== 'swap' || !tx.coinIn || !tx.datum) continue;
    const loc = String(tx.exchange || tx.platform || '').trim();
    const day = (tx.effectiveTimestamp || tx.ts || tx.datum).slice(0, 10);
    const key = `${tx.coinIn}|${loc}|${day}`;
    if (!swapsByCoinLocDay.has(key)) swapsByCoinLocDay.set(key, tx);
  }
  const predOf = new Map(); // outgoing TX-id → predecessor swap TX-id
  for (const tx of txs) {
    if (tx.typ !== 'auszahlung') continue;
    const txId = tx._docId || String(tx.id);
    const isMatched = matchedOutIds.has(txId);
    const isExternal = !!tx.externalTransfer;
    if (!isMatched && !isExternal) continue;
    const loc = String(tx.exchange || tx.platform || '').trim();
    const day = (tx.effectiveTimestamp || tx.ts || tx.datum || '').slice(0, 10);
    const swap = swapsByCoinLocDay.get(`${tx.coin}|${loc}|${day}`);
    if (!swap) continue;
    const swapId = swap._docId || String(swap.id);
    predOf.set(txId, swapId);
  }
  return predOf;
}

function _buildSort(matchedOutIds, matchedInIds, causalPredOf = new Map()) {
  // Liefert ms NUR wenn die TX eine PRAEZISE Zeit traegt (zeit-Feld oder
  // effectiveTimestamp). Pures datum reicht NICHT — zaehlt als imprecise.
  const preciseTs = (tx) => {
    const ts = tx?.effectiveTimestamp || tx?.ts;
    if (ts) {
      const ms = Date.parse(ts);
      if (!Number.isNaN(ms)) return ms;
    }
    if (tx?.datum && tx?.zeit && /^\d{2}:\d{2}(:\d{2})?/.test(tx.zeit)) {
      const z = tx.zeit.length >= 8 ? tx.zeit : `${tx.zeit}:00`;
      const ms = Date.parse(`${tx.datum}T${z}Z`);
      if (!Number.isNaN(ms)) return ms;
    }
    return null;
  };
  // Anchor-Sort fuer matched Transfer-Pairs ohne explizite zeit:
  //   Wenn eine matched-Auszahlung keine eigene Uhrzeit hat, aber ihre
  //   Einzahlung hat eine → setze Auszahlungs-sortKey auf pair.in.ts − 1ms.
  //   Damit ist die kausale Reihenfolge aus → ein selbst dann garantiert, wenn
  //   typPrio (matched-aus=1, matched-ein=2) im eitherImprecise-Branch greifen
  //   wuerde — sortKey-Anchor liegt fest VOR der einzahlung.
  // Symmetrisch fuer matched-Einzahlung ohne zeit: pair.out.ts + 1ms.
  const sortKey = (tx) => {
    const own = preciseTs(tx);
    if (own !== null) return own;

    const txId = tx._docId || String(tx.id);
    if (tx.typ === 'auszahlung') {
      const pair = matchedOutIds.get(txId);
      if (pair) {
        const inTs = preciseTs(pair.in);
        if (inTs !== null) return inTs - 1;
      }
    } else if (tx.typ === 'einzahlung') {
      const pair = matchedInIds.get(txId);
      if (pair) {
        const outTs = preciseTs(pair.out);
        if (outTs !== null) return outTs + 1;
      }
    }
    return tsMillis(tx);
  };
  const rawDayKey = (tx) => {
    if (tx.effectiveTimestamp) return tx.effectiveTimestamp.slice(0, 10);
    if (tx.ts) return tx.ts.slice(0, 10);
    return tx.datum || '';
  };
  // Kausale Sortier-Garantie fuer matched-Pairs:
  // Eine matched einzahlung darf NIEMALS vor ihrer auszahlung laufen, sonst
  // rennt completeTransfer auf leerem pendingTransfer-Pool → costBasisUnknown-
  // Gap-Lot wird erzeugt UND die auszahlung sperrt spaeter Lots, die nie
  // restored werden (pendingTransfer-Leiche). Loesung: dayKey der einzahlung
  // wird auf MAX(eigenes Datum, auszahlung-Datum) angehoben — unabhaengig vom
  // user-eingegebenen Datum.
  const dayKey = (tx) => {
    const baseDay = rawDayKey(tx);
    if (tx.typ === 'einzahlung') {
      const txId = tx._docId || String(tx.id);
      const pair = matchedInIds.get(txId);
      if (pair) {
        const outDay = rawDayKey(pair.out);
        if (outDay && outDay > baseDay) return outDay;
      }
    }
    return baseDay;
  };
  const precRank = (tx) => {
    if (tx.effectiveTimestamp) return 0;
    // Matched-Pair Anchor: wenn diese TX ueber den Pair-Partner eine praezise
    // Zeit erbt (siehe sortKey-Anchor), behandle sie als praezise (precRank=0).
    // MUSS VOR dem tsPrecision-Return stehen — sonst greift bei einer Auszahlung
    // ohne eigene Uhrzeit aber mit tsPrecision='day' der fruehe Return, der
    // Anker wird nie erreicht, und der eitherImprecise+typPrio-Pfad reiht die
    // Einzahlung VOR die Auszahlung. Das laesst completeTransfer auf einen
    // leeren pendingTransfer-Pool rennen → Phantom-Gap-Lot mit
    // costBasisUnknown=true. Reale Symptome (XRP/XLM): 8633 XRP, 2500 XLM zuviel.
    const txId = tx._docId || String(tx.id);
    if (tx.typ === 'auszahlung') {
      const pair = matchedOutIds.get(txId);
      if (pair && preciseTs(pair.in) !== null) return 0;
    } else if (tx.typ === 'einzahlung') {
      const pair = matchedInIds.get(txId);
      if (pair && preciseTs(pair.out) !== null) return 0;
    }
    if (tx.tsPrecision) return tsPrecisionRank(tx.tsPrecision);
    if (tx.zeit && /^\d{2}:\d{2}(:\d{2})?/.test(tx.zeit)) {
      return tx.zeit.length >= 8 ? 0 : 1;
    }
    return 2;
  };

  return (a, b) => {
    const da = dayKey(a), db = dayKey(b);
    if (da !== db) return da < db ? -1 : 1;

    // Special-Case: konkret gepaarte Auszahlung+Einzahlung. Die auszahlung MUSS
    // vor der einzahlung laufen, sonst rennt completeTransfer auf leerem
    // pendingTransfer-Pool und es entsteht ein spurioser costBasisUnknown-Gap-Lot
    // mit Datum=einzahlung-Datum. Ueberschreibt Zeit/typPrio nur fuer DIESES Paar.
    const aId = a._docId || String(a.id);
    const bId = b._docId || String(b.id);
    if (a.typ === 'auszahlung' && matchedOutIds.has(aId)) {
      const pair = matchedOutIds.get(aId);
      const partnerId = pair.in._docId || String(pair.in.id);
      if (b.typ === 'einzahlung' && partnerId === bId) return -1;
    }
    if (b.typ === 'auszahlung' && matchedOutIds.has(bId)) {
      const pair = matchedOutIds.get(bId);
      const partnerId = pair.in._docId || String(pair.in.id);
      if (a.typ === 'einzahlung' && partnerId === aId) return 1;
    }

    // Causal-Predecessor: matched-aus / external-aus MUSS NACH dem swap laufen,
    // der ihren Coin am gleichen Tag/Wallet erst erzeugt. Sonst markForTransfer
    // auf leerem Pool → debt-Lot + Gap-Lot bei einzahlung = Doppelbestand.
    if (causalPredOf.get(aId) === bId) return 1;  // a hat b als swap-Vorgänger
    if (causalPredOf.get(bId) === aId) return -1;

    const pa = precRank(a), pb = precRank(b);
    const eitherImprecise = pa >= 2 || pb >= 2;
    if (eitherImprecise) {
      const tpa = _typePrio(a, aId, matchedOutIds, matchedInIds);
      const tpb = _typePrio(b, bId, matchedOutIds, matchedInIds);
      if (tpa !== tpb) return tpa - tpb;
    }

    const ka = sortKey(a), kb = sortKey(b);
    if (ka !== kb) return ka - kb;

    if (pa !== pb) return pa - pb;

    const oa = a.manualOrderIndex ?? Infinity, ob = b.manualOrderIndex ?? Infinity;
    if (oa !== ob) return oa - ob;

    const tpa = _typePrio(a, aId, matchedOutIds, matchedInIds);
    const tpb = _typePrio(b, bId, matchedOutIds, matchedInIds);
    if (tpa !== tpb) return tpa - tpb;

    return aId < bId ? -1 : aId > bId ? 1 : 0;
  };
}

/**
 * Kern-Engine: Verarbeitet alle Transaktionen und baut den FIFO-Lot-Pool auf.
 * Liefert sowohl die angereicherten Event-Results (für Steuer/Reports) als auch
 * den finalen Pool (für Bestands-/Investment-/Haltefrist-Berechnungen).
 *
 * Single Source of Truth: lotSystem.js ist nur noch ein Format-Adapter.
 *
 * @param {Array} txs Rohe Transaktionen aus Firestore
 * @returns {{ results: Array, pool: Object, transferPairs: Array }}
 *   results:       Alle TX, steuer-relevante Events sind angereichert (ak, gv, hf, stpfl, …)
 *   pool:          { [coin]: Lot[] } — finaler FIFO-Pool. Lot-Felder:
 *                  { lotId, sourceTransactionId, datum, rest, originalAmount,
 *                    stueck, location, costBasisUnknown, overConsumption }
 *   transferPairs: Ergebnis von matchTransfers() für nachgelagerte Konsumenten
 */
export function buildFifoPool(txs) {
  const normalized  = normalizeTransactions(txs);
  const pairs       = matchTransfers(normalized);

  const pool    = {};
  const results = [];

  const matchedOutIds = new Map(pairs.map(p => [p.out._docId || String(p.out.id), p]));
  const matchedInIds  = new Map(pairs.map(p => [p.in._docId  || String(p.in.id),  p]));

  const causalPredOf = _buildCausalSwapPredecessors(normalized, matchedOutIds);
  const sortFn = _buildSort(matchedOutIds, matchedInIds, causalPredOf);

  // KANONISCHER PRE-SORT (Determinismus-Garantie, Invariant 7):
  // _buildSort hat mehrere Spezialfall-Returns (matched-Pair, causal-predecessor)
  // und ist dadurch NICHT garantiert transitiv. Ein nicht-transitiver Komparator
  // liefert in V8 je nach EINGABE-Reihenfolge unterschiedliche Ergebnisse. Firestore
  // liefert TXs mit orderBy('datum','desc') — in dieser Reihenfolge brach die
  // Garantie "matched-Auszahlung vor matched-Einzahlung" → completeTransfer lief
  // ins Leere → Orphan-Restore + Gap-Lot = Doppelbestand (real: XRP 8001→16622).
  //
  // Fix: zuerst stabil chronologisch aufsteigend (tsMillis, dann _docId) sortieren.
  // V8s Array.sort ist stabil → der nachfolgende sortFn bekommt IMMER dieselbe
  // Start-Permutation, unabhaengig von der Firestore-Reihenfolge → deterministisch.
  // Die chronologisch-aufsteigende Basis ist die korrekte FIFO-Reihenfolge.
  // effTs: kanonischer Zeit-Schlüssel mit Transfer-Anker. Eine matched
  // Einzahlung bekommt den Zeitstempel IHRER Auszahlung + 1ms — so steht sie
  // garantiert direkt NACH der Auszahlung, auch wenn ihr realer Timestamp davor
  // liegt (reale Quirks: Ledger bestätigt Eingang Minuten bevor die Börse die
  // Auszahlung bucht). Ein einzelner numerischer Schlüssel ist IMMER transitiv —
  // im Gegensatz zu den pairwise-Spezialfällen in _buildSort, die bei ungünstiger
  // Eingabe-Reihenfolge einen Sortier-Zyklus erzeugten (XRP 8001→16622).
  // Swap-Lookup für den kausalen Anker (matched/externe Auszahlung NACH dem
  // Swap, der ihren Coin am selben Tag/Wallet erst erzeugt).
  const _swapById = new Map(
    normalized.filter(t => t.typ === 'swap').map(t => [t._docId || String(t.id), t])
  );
  // Kausaler Anker einer Auszahlung: liegt am selben Tag/Wallet ein Swap, der
  // ihren ausgezahlten Coin erst PRODUZIERT (causalPredOf), wird die Auszahlung
  // hinter diesen Swap verankert. Sonst läuft die atomare Transfer-Abwicklung
  // (_processTransfer → markForTransfer) auf einen leeren Pool → Debt-Lot +
  // Gap-Lot = Doppelbestand (S6a: Swap+Transfer am gleichen Tag ohne zeit-Feld,
  // Bitpanda-Pattern). Als EINZELNER numerischer Schlüssel immer transitiv —
  // im Gegensatz zum pairwise causal-Spezialfall in _buildSort, der bei der
  // canonicalCmp-Start-Permutation (Auszahlung vor Swap per _docId-Tiebreaker)
  // die Reihenfolge nicht zuverlässig dreht.
  const _anchorTs = (tx) => {
    const id = tx._docId || String(tx.id);
    if (tx.typ === 'auszahlung' && causalPredOf.has(id)) {
      const swap = _swapById.get(causalPredOf.get(id));
      const own = tsMillis(tx);
      const swapTs = swap ? tsMillis(swap) : null;
      const base = (Number.isFinite(swapTs) && swapTs > own) ? swapTs : own;
      return base + 1;
    }
    return tsMillis(tx);
  };
  const effTs = (tx) => {
    const id = tx._docId || String(tx.id);
    if (tx.typ === 'einzahlung' && matchedInIds.has(id)) {
      const pair = matchedInIds.get(id);
      const outAnchor = _anchorTs(pair.out);
      if (Number.isFinite(outAnchor) && outAnchor > 0) return outAnchor + 1;
    }
    return _anchorTs(tx);
  };
  const canonicalCmp = (a, b) => {
    const ta = effTs(a), tb = effTs(b);
    if (ta !== tb) return ta - tb;
    const ida = a._docId || String(a.id || '');
    const idb = b._docId || String(b.id || '');
    return ida < idb ? -1 : ida > idb ? 1 : 0;
  };

  // ── Atomare Transfer-Verarbeitung (order-unabhängig) ──────────────────────
  // Ein gematchtes auszahlung↔einzahlung-Paar wird KOMPLETT abgewickelt, sobald
  // die ERSTE der beiden Seiten in der Verarbeitung erreicht wird (idempotent
  // via _transferDone). Vorher lief markForTransfer (Auszahlung) und
  // completeTransfer (Einzahlung) getrennt — wenn die Sortierung die Einzahlung
  // VOR die Auszahlung stellte (reale Timestamps: Ledger-IN vor Börsen-OUT),
  // lief completeTransfer ins Leere → Gap-Lot + Orphan-Restore = Doppelbestand
  // (XRP 8001→16363). Jetzt egal welche Seite zuerst kommt: die Quell-Lots
  // (Käufe liegen auf früheren Tagen, also immer schon im Pool) werden reserviert
  // UND sofort ans Ziel-Wallet übertragen. Kein Orphan, kein Phantom.
  const _transferDone = new Set();
  const _processTransfer = (pair) => {
    const out = pair.out, inn = pair.in;
    const outId = out._docId || String(out.id);
    if (_transferDone.has(outId)) return;
    _transferDone.add(outId);
    const coin  = out.coin || out.sym || inn.coin || inn.sym;
    if (!coin) return;
    if (!pool[coin]) pool[coin] = [];
    const outLoc  = _loc(out);
    const inLoc   = _loc(inn);
    const inMenge = D(inn.menge);
    // 1. Quell-Lots reservieren (wallet-spezifisch, gleiche Hybrid-Fallback-Logik)
    const unmatchedOut = markForTransfer(pool, coin, D(out.menge).toNumber(), outId, outLoc);
    if (unmatchedOut > 1e-9) recordOverConsumption(pool, coin, unmatchedOut, out, 'auszahlung-matched', outLoc);
    // 2. Reservierte Lots ans Ziel-Wallet übertragen (Datum/Stückpreis bleiben)
    const restored = completeTransfer(pool, coin, outId, inLoc);
    // 3. Netzwerkgebühr: gesendet > angekommen → Differenz am Ziel verbrauchen
    const transferFee = D(restored).minus(inMenge);
    if (transferFee.gt(D_EPSILON)) fifoMatch(pool, coin, transferFee.toNumber(), inn.datum, inLoc);
    // 4. Gap: angekommen > restored (nicht gedeckt) → neues Lot am Ziel
    const gap = inMenge.minus(D(restored));
    if (gap.gt(D_EPSILON)) {
      const totalCost = D(inn.preis).gt(D_ZERO)
        ? D(inn.preis).times(gap).div(inMenge)
        : D(inn.countervalue || 0).gt(D_ZERO) && inMenge.gt(D_ZERO)
          ? D(inn.countervalue).times(gap).div(inMenge)
          : D_ZERO;
      const hasPrice = totalCost.gt(D_ZERO) && gap.gt(D_ZERO);
      const inId = inn._docId || String(inn.id);
      pool[coin].push({
        lotId:               `dep_${inId}`,
        sourceTransactionId: inId,
        datum:               inn.datum,
        rest:                gap.toNumber(),
        originalAmount:      gap.toNumber(),
        stueck:              hasPrice ? totalCost.div(gap).toNumber() : 0,
        costBasisUnknown:    !hasPrice,
        overConsumption:     false,
        location:            inLoc,
      });
    }
  };

  [...normalized]
    .sort(canonicalCmp)
    .sort(sortFn)
    .forEach(tx => {
      const txId    = tx._docId || String(tx.id);
      const gebuehr = D(tx.gebuehr);
      const loc     = _loc(tx);
      if (tx.coin == null) tx.coin = tx.sym ?? tx.asset ?? null;

      // ── Kauf ─────────────────────────────────────────────────────────────────
      if (tx.typ === 'kauf') {
        if (!pool[tx.coin]) pool[tx.coin] = [];
        const totalCost = D(tx.preis).plus(gebuehr);
        const menge     = D(tx.menge);
        const hasPrice  = menge.gt(D_ZERO) && totalCost.gt(D_ZERO);
        pool[tx.coin].push({
          lotId:               txId,
          sourceTransactionId: txId,
          datum:               tx.datum,
          rest:                menge.toNumber(),
          originalAmount:      menge.toNumber(),
          stueck:              hasPrice ? totalCost.div(menge).toNumber() : 0,
          costBasisUnknown:    !hasPrice,
          overConsumption:     false,
          location:            loc,
        });
        results.push({ ...tx, gv: null });

      // ── Einkommenstypen ───────────────────────────────────────────────────────
      } else if (INCOME_TYPES.has(tx.typ)) {
        if (!pool[tx.coin]) pool[tx.coin] = [];
        const totalCost = D(tx.preis).plus(gebuehr);
        const menge     = D(tx.menge);
        const hasPrice  = menge.gt(D_ZERO) && totalCost.gt(D_ZERO);
        pool[tx.coin].push({
          lotId:               `${tx.typ}_${txId}`,
          sourceTransactionId: txId,
          datum:               tx.datum,
          rest:                menge.toNumber(),
          originalAmount:      menge.toNumber(),
          stueck:              hasPrice ? totalCost.div(menge).toNumber() : 0,
          costBasisUnknown:    !hasPrice,
          overConsumption:     false,
          location:            loc,
        });
        results.push({ ...tx, gv: null });

      // ── Swap ──────────────────────────────────────────────────────────────────
      } else if (tx.typ === 'swap') {
        // Null-Coin-Guard: ohne coinOut/coinIn ist der Swap unverarbeitbar.
        // Wuerde sonst pool[null] erzeugen und die Aggregation in null-Buckets
        // verschmutzen (siehe Audit-Hypothese 4).
        if (!tx.coinOut || !tx.coinIn) {
          // eslint-disable-next-line no-console
          console.warn(`[fifo] Swap ohne coinOut/coinIn uebersprungen (TX ${txId})`);
          results.push({ ...tx, gv: null, costBasisUnknown: true, incomplete: true });
          return;
        }
        // Stablecoin-Auto-Konversion: wenn tx.preis fehlt UND eine Seite des Swaps
        // ist ein Stablecoin (USDC/USDT/DAI/EURC/...), leiten wir den EUR-Wert per
        // Pegging-Constitution handfest ab — KEIN Marktpreis, keine Schätzung.
        let effectivePreis = D(tx.preis);
        let priceSource = effectivePreis.gt(D_ZERO) ? undefined : null;
        if (!effectivePreis.gt(D_ZERO)) {
          // Fiat-Quote-Direkt: bei XLM→EUR ist mengeIn der EUR-Erloes;
          // bei EUR→XLM ist mengeOut die EUR-Anschaffung. Keine Schaetzung.
          const coinInUp  = String(tx.coinIn  || '').toUpperCase();
          const coinOutUp = String(tx.coinOut || '').toUpperCase();
          if (FIFO_FIAT_QUOTES.has(coinInUp) && D(tx.mengeIn).gt(D_ZERO)) {
            effectivePreis = D(tx.mengeIn);
            priceSource = 'fiat-quote';
          } else if (FIFO_FIAT_QUOTES.has(coinOutUp) && D(tx.mengeOut).gt(D_ZERO)) {
            effectivePreis = D(tx.mengeOut);
            priceSource = 'fiat-quote';
          } else {
            const resolved = resolveSwapEurValue(tx.coinOut, tx.mengeOut, tx.coinIn, tx.mengeIn);
            if (resolved) {
              effectivePreis = D(resolved.value);
              priceSource = resolved.source;
            }
          }
        }

        // Wallet-spezifisch: nur Coins aus DIESEM Wallet (tx.exchange) verbrauchen
        const matchResult = fifoMatch(pool, tx.coinOut, tx.mengeOut, tx.datum, loc);
        const { ak, kd, hf, incomplete, matched, target, costBasisUnknown, soldLots, fallbackUsed, unmatched } = matchResult;
        if (unmatched > 1e-9) recordOverConsumption(pool, tx.coinOut, unmatched, tx, 'swap-out', loc);
        if (!incomplete) {
          const netProceeds = effectivePreis.minus(gebuehr);
          const proportion = target > 0 ? D(matched).div(D(target)) : D_ZERO;
          const partialProceeds = netProceeds.times(proportion);
          const gv = costBasisUnknown
            ? null
            : partialProceeds.minus(D(ak)).toDecimalPlaces(2).toNumber();
          results.push({
            ...tx,
            kaufDatum:        kd,
            ak:               D(ak).toDecimalPlaces(2).toNumber(),
            hf,
            stpfl:            isWithinSpekulationsfrist(kd, tx.datum),
            gv,
            incomplete:       undefined,
            costBasisUnknown: costBasisUnknown || undefined,
            soldLots,
            fallbackUsed:     fallbackUsed || undefined,
            effectiveErloes:  effectivePreis.toDecimalPlaces(2).toNumber(),
            priceSource:      priceSource || undefined,
          });
        } else {
          // Teil-/Über-Abgang: gedeckter Anteil + ungedeckter Rest (Anschaffung offen).
          emitSplitDisposal(
            results, matchResult, tx, effectivePreis, gebuehr,
            (net, akD) => net.minus(akD).toDecimalPlaces(2).toNumber(),
            (seg) => ({
              ...tx,
              mengeOut:         seg.menge,
              mengeIn:          D(tx.mengeIn).times(seg.prop).toDecimalPlaces(8).toNumber(),
              kaufDatum:        seg.kaufDatum,
              ak:               seg.ak,
              hf:               seg.hf,
              stpfl:            seg.stpfl,
              gv:               seg.gv,
              preis:            seg.preis,
              effectiveErloes:  seg.preis,
              gebuehr:          seg.gebuehr,
              incomplete:       seg.incomplete,
              costBasisUnknown: seg.costBasisUnknown,
              soldLots:         seg.soldLots,
              fallbackUsed:     seg.fallbackUsed,
              priceSource:      priceSource || undefined,
            })
          );
        }
        if (!pool[tx.coinIn]) pool[tx.coinIn] = [];
        const mengeIn  = D(tx.mengeIn);
        const hasPrice = mengeIn.gt(D_ZERO) && effectivePreis.gt(D_ZERO);
        // Der IN-Lot landet im SELBEN Wallet wie der Swap (location = loc).
        pool[tx.coinIn].push({
          lotId:               `swap_${txId}`,
          sourceTransactionId: txId,
          datum:               tx.datum,
          rest:                mengeIn.toNumber(),
          originalAmount:      mengeIn.toNumber(),
          stueck:              hasPrice ? effectivePreis.div(mengeIn).toNumber() : 0,
          costBasisUnknown:    !hasPrice,
          overConsumption:     false,
          location:            loc,
        });

      // ── Verkauf ───────────────────────────────────────────────────────────────
      } else if (tx.typ === 'verkauf') {
        const matchResult = fifoMatch(pool, tx.coin, tx.menge, tx.datum, loc);
        const { ak, kd, hf, incomplete, matched, target, costBasisUnknown, soldLots, fallbackUsed, unmatched } = matchResult;
        if (unmatched > 1e-9) recordOverConsumption(pool, tx.coin, unmatched, tx, 'verkauf', loc);
        if (!incomplete) {
          const netProceeds = D(tx.preis).minus(gebuehr);
          const proportion = target > 0 ? D(matched).div(D(target)) : D_ZERO;
          const partialProceeds = netProceeds.times(proportion);
          const gv = costBasisUnknown
            ? null
            : partialProceeds.minus(D(ak)).toDecimalPlaces(2).toNumber();
          results.push({
            ...tx,
            kaufDatum:        kd,
            ak:               D(ak).toDecimalPlaces(2).toNumber(),
            hf,
            stpfl:            isWithinSpekulationsfrist(kd, tx.datum),
            gv,
            incomplete:       undefined,
            costBasisUnknown: costBasisUnknown || undefined,
            soldLots,
            fallbackUsed:     fallbackUsed || undefined,
          });
        } else {
          // Teil-/Über-Verkauf: gedeckter Anteil mit realem gv + ungedeckter Rest
          // als eigenes costBasisUnknown-Event (sonst verschwand die Veräußerung).
          emitSplitDisposal(
            results, matchResult, tx, D(tx.preis), gebuehr,
            (net, akD) => net.minus(akD).toDecimalPlaces(2).toNumber(),
            (seg) => ({
              ...tx,
              menge:            seg.menge,
              preis:            seg.preis,
              gebuehr:          seg.gebuehr,
              kaufDatum:        seg.kaufDatum,
              ak:               seg.ak,
              hf:               seg.hf,
              stpfl:            seg.stpfl,
              gv:               seg.gv,
              incomplete:       seg.incomplete,
              costBasisUnknown: seg.costBasisUnknown,
              soldLots:         seg.soldLots,
              fallbackUsed:     seg.fallbackUsed,
            })
          );
        }

      // ── Auszahlung ────────────────────────────────────────────────────────────
      } else if (tx.typ === 'auszahlung') {
        if (!pool[tx.coin]) pool[tx.coin] = [];
        if (matchedOutIds.has(txId)) {
          // Atomare Abwicklung (idempotent) — egal ob diese Auszahlung oder
          // ihre Einzahlung zuerst erreicht wird.
          _processTransfer(matchedOutIds.get(txId));
          results.push({ ...tx, gv: null });
        } else if (tx.externalTransfer) {
          // Externer Outflow (Freund / Geschenk / privater Verkauf):
          // Coins endgültig weg → Lots werden via fifoMatch konsumiert. Kein
          // EUR-Erlös erfasst → gv = null. Wenn der User später einen Preis
          // nachträgt, sollte die TX als 'verkauf' klassifiziert werden.
          const r = fifoMatch(pool, tx.coin, tx.menge, tx.datum, loc);
          if (r.incomplete) recordOverConsumption(pool, tx.coin, r.unmatched ?? 0, tx, 'auszahlung-external', loc);
          results.push({
            ...tx,
            kaufDatum:        r.kd,
            ak:               D(r.ak).toDecimalPlaces(2).toNumber(),
            hf:               r.hf,
            gv:               null,
            soldLots:         r.soldLots,
            costBasisUnknown: r.costBasisUnknown || undefined,
            externalOutflow:  true,
          });
        } else {
          // Unmatched Auszahlung ohne externalTransfer-Flag: vermutlich Transfer
          // zu eigenem Wallet ohne erfasste Gegenseite. Lots intakt lassen.
          results.push({ ...tx, gv: null, unmatchedWithdrawal: true });
        }

      // ── Einzahlung ────────────────────────────────────────────────────────────
      } else if (tx.typ === 'einzahlung') {
        if (!pool[tx.coin]) pool[tx.coin] = [];
        if (matchedInIds.has(txId)) {
          // Atomare Abwicklung (idempotent) — falls die Auszahlung schon lief,
          // ist der Transfer bereits komplett; sonst wird er hier ausgelöst.
          // markForTransfer + completeTransfer + Fee + Gap laufen in EINER
          // order-unabhängigen Einheit (_processTransfer).
          _processTransfer(matchedInIds.get(txId));
        } else {
          const menge = D(tx.menge);
          const totalCost = D(tx.preis).gt(D_ZERO)
            ? D(tx.preis).plus(gebuehr)
            : D(tx.countervalue || 0).gt(D_ZERO)
              ? D(tx.countervalue).plus(gebuehr)
              : D_ZERO;
          const hasPrice = menge.gt(D_ZERO) && totalCost.gt(D_ZERO);
          pool[tx.coin].push({
            lotId:               `dep_${txId}`,
            sourceTransactionId: txId,
            datum:               tx.datum,
            rest:                menge.toNumber(),
            originalAmount:      menge.toNumber(),
            stueck:              hasPrice ? totalCost.div(menge).toNumber() : 0,
            costBasisUnknown:    !hasPrice,
            overConsumption:     false,
            location:            loc,
          });
        }
        results.push({ ...tx, gv: null });

      // ── Burn ──────────────────────────────────────────────────────────────────
      } else if (tx.typ === 'burn') {
        if (!pool[tx.coin]) pool[tx.coin] = [];
        const { unmatched } = fifoMatch(pool, tx.coin, tx.menge, tx.datum, loc);
        if (unmatched > 1e-9) recordOverConsumption(pool, tx.coin, unmatched, tx, 'burn', loc);
        results.push({ ...tx, gv: null });

      // ── Verlust / Diebstahl / Hack ────────────────────────────────────────────
      } else if (tx.typ === 'loss') {
        if (!pool[tx.coin]) pool[tx.coin] = [];
        const matchResult = fifoMatch(pool, tx.coin, tx.menge, tx.datum, loc);
        const { ak, kd, hf, incomplete, costBasisUnknown, soldLots, fallbackUsed, unmatched } = matchResult;
        if (unmatched > 1e-9) recordOverConsumption(pool, tx.coin, unmatched, tx, 'loss', loc);
        if (!incomplete) {
          const gv = costBasisUnknown
            ? null
            : D(ak).neg().toDecimalPlaces(2).toNumber();
          results.push({
            ...tx,
            kaufDatum:        kd,
            ak:               D(ak).toDecimalPlaces(2).toNumber(),
            hf,
            stpfl:            isWithinSpekulationsfrist(kd, tx.datum),
            gv,
            incomplete:       undefined,
            costBasisUnknown: costBasisUnknown || undefined,
            soldLots,
            fallbackUsed:     fallbackUsed || undefined,
          });
        } else {
          // Teil-Verlust über Bestand: gedeckter Anteil gv=−ak, Rest Anschaffung offen.
          emitSplitDisposal(
            results, matchResult, tx, D_ZERO, D_ZERO,
            (_net, akD) => akD.neg().toDecimalPlaces(2).toNumber(),
            (seg) => ({
              ...tx,
              menge:            seg.menge,
              kaufDatum:        seg.kaufDatum,
              ak:               seg.ak,
              hf:               seg.hf,
              stpfl:            seg.stpfl,
              gv:               seg.gv,
              incomplete:       seg.incomplete,
              costBasisUnknown: seg.costBasisUnknown,
              soldLots:         seg.soldLots,
              fallbackUsed:     seg.fallbackUsed,
            })
          );
        }

      // ── Futures PnL / Funding-Fee — Lot-Effekt auf Margin-Coin (meist USDT) ────
      // preis vorzeichenbehaftet: negativ = Verlust/Kosten → Lots konsumieren
      //                           positiv = Gewinn           → neues Lot anlegen
      // menge = absoluter Coin-Betrag; Fallback auf |preis| wenn menge nicht gesetzt
      } else if (tx.typ === 'futures_pnl' || tx.typ === 'funding_fee') {
        const pnl = D(tx.preis || 0);
        if (tx.coin) {
          if (!pool[tx.coin]) pool[tx.coin] = [];
          const coinAmt = D(tx.menge).gt(D_ZERO) ? D(tx.menge) : pnl.abs();
          if (pnl.lt(D_ZERO) && coinAmt.gt(D_EPSILON)) {
            const { unmatched } = fifoMatch(pool, tx.coin, coinAmt.toNumber(), tx.datum, loc);
            if (unmatched > 1e-9) recordOverConsumption(pool, tx.coin, unmatched, tx, tx.typ, loc);
          } else if (pnl.gt(D_ZERO) && coinAmt.gt(D_EPSILON)) {
            pool[tx.coin].push({
              lotId:               `${tx.typ}_${txId}`,
              sourceTransactionId: txId,
              datum:               tx.datum,
              rest:                coinAmt.toNumber(),
              originalAmount:      coinAmt.toNumber(),
              stueck:              pnl.div(coinAmt).toNumber(),
              costBasisUnknown:    false,
              overConsumption:     false,
              location:            loc,
            });
          }
        }
        results.push({
          ...tx,
          ak:        0,
          kaufDatum: tx.datum,
          hf:        0,
          stpfl:     true,
          gv:        pnl.toDecimalPlaces(2).toNumber(),
        });

      // ── Gebühr (Coin-Fee) — konsumiert Lots aus dem Pool ─────────────────────
      } else if (tx.typ === 'gebuehr') {
        if (tx.coin && D(tx.menge).gt(D_EPSILON)) {
          if (!pool[tx.coin]) pool[tx.coin] = [];
          const { unmatched } = fifoMatch(pool, tx.coin, D(tx.menge).toNumber(), tx.datum, loc);
          if (unmatched > 1e-9) recordOverConsumption(pool, tx.coin, unmatched, tx, 'gebuehr', loc);
        }
        results.push({ ...tx, gv: null });

      } else {
        results.push({ ...tx, gv: null });
      }
    });

  // Defensive Orphan-pendingTransfer-Aufloesung. Eigentlich garantiert die
  // Sort-Reihenfolge (auszahlung vor matched einzahlung), dass completeTransfer
  // jedes markForTransfer aufloest. Falls aber doch ein Lot mit pendingTransfer>0
  // uebrig bleibt, restaurieren wir lot.rest, damit der Bestand nicht stille
  // verzerrt wird (vgl. Audit-Hypothese: lot.rest bleibt sonst dekrementiert
  // und Dashboard/Reports zeigen zu wenig Bestand).
  for (const coin of Object.keys(pool)) {
    for (const lot of pool[coin]) {
      if ((lot.pendingTransfer || 0) > 1e-9) {
        // eslint-disable-next-line no-console
        console.warn(`[fifo] Orphan pendingTransfer ${lot.pendingTransfer} restauriert (${coin}, lot ${lot.lotId})`);
        lot.rest = D(lot.rest).plus(D(lot.pendingTransfer)).toNumber();
        lot.pendingTransfer = 0;
        lot.transferOutId = null;
      }
    }
  }

  return { results, pool, transferPairs: pairs };
}

/**
 * Führt FIFO-Berechnung für alle Transaktionen durch.
 * Trivialer Wrapper um buildFifoPool — nur das results-Array.
 *
 * @param {Array} txs Rohe Transaktionen aus Firestore
 * @returns {Array} Alle Transaktionen, Steuer-Events angereichert mit kaufDatum, ak, hf, stpfl, gv
 */
export function fifo(txs) {
  return buildFifoPool(txs).results;
}

/**
 * Baut den kausalen Sortier-Komparator für eine TX-Liste (gleich wie buildFifoPool
 * intern verwendet). Damit zeigen Display-Listen die gleiche Reihenfolge wie FIFO
 * sie verarbeitet hat — am gleichen Tag: matched-aus → matched-ein → kauf/income
 * → swap → verkauf → unmatched-aus.
 *
 * @param {Array} txs Die volle TX-Liste (Universum für Transfer-Pair-Matching)
 * @returns {(a, b) => number} Komparator-Funktion für Array.sort
 */
export function buildCausalSort(txs) {
  const normalized = normalizeTransactions(txs);
  const pairs = matchTransfers(normalized);
  const matchedOutIds = new Map(pairs.map(p => [p.out._docId || String(p.out.id), p]));
  const matchedInIds  = new Map(pairs.map(p => [p.in._docId  || String(p.in.id),  p]));
  const causalPredOf  = _buildCausalSwapPredecessors(normalized, matchedOutIds);
  return _buildSort(matchedOutIds, matchedInIds, causalPredOf);
}

/**
 * Filtert FIFO-Ergebnisse auf steuerlich relevante Events.
 * - gv !== null:        normaler Verkauf/Swap mit berechnetem G/V (auch negativ)
 * - costBasisUnknown:   realisierender Event, aber Anschaffung unbekannt → muss
 *                       trotzdem in der Liste auftauchen, damit der User sieht,
 *                       dass hier ein steuerlich relevanter Vorgang stattfand,
 *                       und Daten nachpflegen kann.
 */
export function getTaxableEvents(fifoResults) {
  return fifoResults.filter(tx => tx.gv !== null || tx.costBasisUnknown);
}
